import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import {
  FaProjectDiagram,
  FaUser,
  FaUsers,
  FaCalendar,
  FaEdit,
  FaCheckCircle,
  FaTimes,
  FaCog,
  FaChartBar,
  FaEnvelope,
  FaEye,
  FaSearch,
} from 'react-icons/fa';
import api from '../services/api';
import LoadingSpinner from './LoadingSpinner';

interface UserId {
  _id: string;
  name: string;
  email: string;
  role: string | null;
}

interface ProjectDocument {
  MVP_Scope: { pdf: string | null };
  ICP_Report: { pdf: string | null };
  ICP_Interview_Guide: { pdf: string | null };
  Value_Propositions_Report: { pdf: string | null };
  Competitor_Snapshot: { pdf: string | null };
  GTM_Strategy: { pdf: string | null };
  investorDeck: {
    pdf: string | null;
    ppt: string | null;
    pptStatus: string;
    pptError: string | null;
  };
  financialProjection: {
    summary: string | null;
    excel: string | null;
    inputs: string | null;
    projections: any[];
    generatedAt: string | null;
  };
}

interface StageScores {
  generateCoreQuestion: number | null;
  generateUserPersonas_beforelogin: number | null;
  generateFollowUpQuestion: number | null;
  generatePlatformInformation: number | null;
  generateUserPersonas_afterlogin: number | null;
  generateValueProps: number | null;
  generateFeatures: number | null;
  generateRequirements: number | null;
}

interface Project {
  _id: string;
  projectName: string;
  description: string;
  userId: UserId;
  status: string;
  projectStatus: string;
  isDeleted: boolean;
  document: ProjectDocument;
  stageScores: StageScores;
  qualityScore: number;
  currentDocumentVersion: number;
  platformInfo: Array<{
    platform_name: string;
    platform_type: string;
    user_roles: string[];
    non_technical_details: string;
    technical_details: string;
  }>;
  userPersona: Array<{
    name: string;
    demographics: string[];
    behaviors: string[];
    goals: string[];
    pain_points: string[];
  }>;
  valueProps: Array<{
    persona: string;
    value_proposition: string;
  }>;
  features: Array<{
    name: string;
    priority: string;
    technical_details: string;
    non_technical_details: string;
  }>;
  requirements: Array<{
    domain_name: string;
    functional_requirements: string[];
    non_functional_requirements: string[];
    technical_requirements: string[];
    complexity_assessment: {
      overall_complexity: string;
      reasoning: string;
      risk_factors: string[];
    };
    third_party_services: string[];
  }>;
  coreBusinessQuestion: Array<{
    questionId: string;
    answer: string;
    answerArr: string[];
    _id: string;
  }>;
  followUpQuestion: Array<{
    question: string;
    suggestedAnswer: string;
    answer: string;
    _id: string;
  }>;
  competitors: any[];
  ads: {
    meta?: {
      instagram?: {
        content: string;
        imageUrl: string | null;
        platform: string;
      };
    };
    google?: {
      content: {
        keywords: string[];
        headlines: string[];
        descriptions: string[];
      };
      imageUrl: string | null;
      platform: string;
    };
    x?: {
      content: string;
      imageUrl: string | null;
      platform: string;
    };
  } | null;
  founderKitPaid: boolean;
  founderSessionPaid: boolean;
  paidAdvisorIds: string[];
  sessionBookings: any[];
  willAnswerFollowUpQuestion: boolean;
  lastDocumentUpdated: string;
  createdAt: string;
  updatedAt: string;
  __v: number;
}

const ADVISOR_NAMES: Record<string, string> = {
  mahesh: 'Mahesh',
  de: 'DE',
};

const DOC_ENTRIES: Array<{ key: keyof ProjectDocument; label: string }> = [
  { key: 'MVP_Scope', label: 'MVP Scope' },
  { key: 'ICP_Report', label: 'ICP Report' },
  { key: 'ICP_Interview_Guide', label: 'ICP Interview Guide' },
  { key: 'Value_Propositions_Report', label: 'Value Propositions Report' },
  { key: 'Competitor_Snapshot', label: 'Competitor Snapshot' },
  { key: 'GTM_Strategy', label: 'GTM Strategy' },
];

const STAGE_SCORE_LABELS: Record<string, string> = {
  generateCoreQuestion: 'Core Questions',
  generateUserPersonas_beforelogin: 'Personas (Pre-login)',
  generateFollowUpQuestion: 'Follow-up Q&A',
  generatePlatformInformation: 'Platform Info',
  generateUserPersonas_afterlogin: 'Personas (Post-login)',
  generateValueProps: 'Value Props',
  generateFeatures: 'Features',
  generateRequirements: 'Requirements',
};

const FRONTEND_BASE_URL = 'foundersmate.com';

const ProjectDetail: React.FC = () => {
  const { projectId } = useParams<{ projectId: string }>();
  const navigate = useNavigate();
  const [project, setProject] = useState<Project | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [hostedLandingUrl, setHostedLandingUrl] = useState<string>('');

  useEffect(() => {
    if (!projectId) return;
    setIsLoading(true);
    api.get(`/admin/projects/${projectId}`)
      .then((response) => setProject(response.data.data || response.data))
      .catch((error) => {
        console.error('Error fetching project details:', error);
        setProject(null);
      })
      .finally(() => setIsLoading(false));
  }, [projectId]);

  useEffect(() => {
    if (!projectId) return;
    let alive = true;
    const fetchLandingUrl = async () => {
      try {
        const response = await api.get(`/admin/projects/${projectId}/landing-page`);
        if (!alive) return;
        const responseData = response?.data?.data ?? response?.data;
        const site = Array.isArray(responseData) ? responseData[0] ?? null : responseData;
        const subdomain = site?.subdomain;
        const customDomain = site?.customDomain;
        const url = customDomain
          ? customDomain.startsWith('http') ? customDomain : `https://${customDomain}`
          : subdomain
          ? `https://${subdomain}.${FRONTEND_BASE_URL}`
          : '';
        setHostedLandingUrl(url);
      } catch (err) {
        console.error('Failed to fetch hosted landing URL:', err);
        if (alive) setHostedLandingUrl('');
      }
    };
    fetchLandingUrl();
    return () => { alive = false; };
  }, [projectId]);

  const formatDate = (dateString: string) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric', month: 'long', day: 'numeric',
      hour: '2-digit', minute: '2-digit',
    });
  };

  const scoreColor = (score: number | null) => {
    if (score === null) return 'text-gray-400';
    if (score >= 80) return 'text-green-600';
    if (score >= 50) return 'text-yellow-600';
    return 'text-red-500';
  };

  const getStatusBadge = (status: string) => {
    const map: Record<string, string> = {
      draft: 'bg-gray-100 text-gray-800',
      published: 'bg-green-100 text-green-800',
      active: 'bg-green-100 text-green-800',
      deleted: 'bg-red-100 text-red-800',
    };
    return (
      <span className={`px-3 py-1 rounded-full text-xs font-medium capitalize ${map[status] || 'bg-gray-100 text-gray-800'}`}>
        {status}
      </span>
    );
  };

  const PaidBadge = ({ paid }: { paid: boolean }) => (
    <span className={`inline-flex items-center gap-1.5 px-3 py-1 rounded-full text-xs font-semibold ${paid ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'}`}>
      {paid
        ? <FaCheckCircle className="h-3 w-3" />
        : <FaTimes className="h-3 w-3" />}
      {paid ? 'Paid' : 'Not Paid'}
    </span>
  );

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-64">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (!project) {
    return (
      <div className="text-center py-16">
        <FaProjectDiagram className="h-12 w-12 mx-auto text-gray-300 mb-4" />
        <p className="text-gray-500 text-lg">Project not found</p>
        <button onClick={() => navigate('/projects')} className="mt-4 text-primary hover:underline text-sm">
          Back to Projects
        </button>
      </div>
    );
  }

  const owner = project.userId;

  return (
    <div className="space-y-6">

      {/* Header */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }}
        className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-6">
          <button
            onClick={() => navigate('/projects')}
            className="p-2 rounded-lg text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
          >
            <FaProjectDiagram className="h-5 w-5" />
          </button>
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Project Details</h1>
            <p className="text-gray-500 text-sm">Comprehensive project overview</p>
          </div>
        </div>

        <div className="bg-gradient-to-r from-primary to-primaryDark rounded-xl p-6 text-white">
          <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
            <div className="flex items-center gap-4">
              <div className="w-14 h-14 bg-white bg-opacity-20 rounded-full flex items-center justify-center flex-shrink-0">
                <FaProjectDiagram className="h-7 w-7" />
              </div>
              <div>
                <h2 className="text-2xl font-bold">{project.projectName}</h2>
                <div className="flex flex-wrap items-center gap-3 mt-2">
                  {getStatusBadge(project.isDeleted ? 'deleted' : project.status)}
                  <span className="text-sm text-white opacity-80 capitalize">
                    {project.projectStatus?.replace(/_/g, ' ')}
                  </span>
                  <span className="text-sm text-white opacity-80">v{project.currentDocumentVersion}</span>
                </div>
              </div>
            </div>
            <div className="text-right space-y-2">
              <div>
                <p className="text-xs text-white opacity-70">Quality Score</p>
                <p className={`text-4xl font-bold ${
                  project.qualityScore >= 80 ? 'text-green-300' :
                  project.qualityScore >= 50 ? 'text-yellow-300' :
                  'text-red-300'
                }`}>
                  {project.qualityScore ?? '—'}
                </p>
              </div>
              {hostedLandingUrl && (
                <a
                  href={hostedLandingUrl}
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-white bg-opacity-20 hover:bg-opacity-30 rounded-lg text-xs font-medium text-white transition-colors"
                >
                  <FaEye className="h-3 w-3" />
                  View Landing Page
                </a>
              )}
            </div>
          </div>
        </div>
      </motion.div>

      {/* Payment & Advisor Status */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.1 }}
        className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-5">
          <FaCheckCircle className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">Payment & Advisor Status</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {/* Founder Kit */}
          <div className="border border-gray-100 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Founder Kit</p>
            <PaidBadge paid={project.founderKitPaid} />
            <p className="text-xs text-gray-400">One-time document generation package</p>
          </div>

          {/* Advisor Session */}
          <div className="border border-gray-100 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Advisor Session</p>
            <PaidBadge paid={project.founderSessionPaid} />
            <p className="text-xs text-gray-400">One-on-one session with an advisor</p>
          </div>

          {/* Paid Advisors */}
          <div className="border border-gray-100 rounded-xl p-4 space-y-2">
            <p className="text-xs font-semibold text-gray-500 uppercase tracking-wide">Paid Advisors</p>
            {(project.paidAdvisorIds ?? []).length > 0 ? (
              <div className="flex flex-wrap gap-2">
                {(project.paidAdvisorIds ?? []).map((id) => (
                  <span key={id} className="inline-flex items-center gap-1 px-2 py-1 bg-blue-100 text-blue-700 rounded-full text-xs font-medium">
                    <FaUser className="h-2.5 w-2.5" />
                    {ADVISOR_NAMES[id] || id}
                  </span>
                ))}
              </div>
            ) : (
              <span className="text-xs text-gray-400">No advisors selected</span>
            )}
            <p className="text-xs text-gray-400">Advisors the user has paid to meet</p>
          </div>
        </div>

        {(project.sessionBookings ?? []).length > 0 && (
          <div className="mt-5 border-t border-gray-100 pt-4">
            <p className="text-sm font-medium text-gray-700 mb-3">Session Bookings</p>
            <div className="space-y-2">
              {(project.sessionBookings ?? []).map((booking: any, i: number) => (
                <div key={i} className="flex items-center justify-between bg-gray-50 rounded-lg px-4 py-2 text-sm">
                  <span className="text-gray-700">{booking.advisorId || `Booking ${i + 1}`}</span>
                  <span className="text-gray-500">{formatDate(booking.scheduledAt || booking.createdAt)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </motion.div>

      {/* Owner & Timeline */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        <motion.div initial={{ opacity: 0, x: -20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaUser className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Project Owner</h3>
          </div>
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 bg-gradient-to-r from-primary to-primaryDark rounded-full flex items-center justify-center text-white font-semibold text-sm flex-shrink-0">
                {owner?.name?.split(' ').map((n: string) => n[0]).join('').toUpperCase() || 'U'}
              </div>
              <div>
                <p className="font-medium text-gray-900">{owner?.name || '—'}</p>
                <p className="text-sm text-gray-500">{owner?.email || '—'}</p>
                <span className={`inline-block mt-1 px-2 py-0.5 rounded-full text-xs font-medium ${
                  owner?.role === 'agency_admin' ? 'bg-blue-100 text-blue-700' :
                  owner?.role === 'member' ? 'bg-gray-100 text-gray-700' :
                  'bg-purple-100 text-purple-700'
                }`}>
                  {owner?.role || 'guest'}
                </span>
              </div>
            </div>
            {owner?._id && (
              <button
                onClick={() => navigate(`/users/${owner._id}`)}
                className="px-3 py-1.5 bg-primary text-white text-sm rounded-lg hover:bg-primaryDark transition-colors"
              >
                View Profile
              </button>
            )}
          </div>
        </motion.div>

        <motion.div initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaCalendar className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Timeline</h3>
          </div>
          <div className="space-y-3">
            {[
              { label: 'Created', value: project.createdAt },
              { label: 'Last Updated', value: project.updatedAt },
              { label: 'Last Document Update', value: project.lastDocumentUpdated },
            ].map(({ label, value }) => (
              <div key={label} className="flex justify-between items-start text-sm">
                <span className="text-gray-500 w-44 flex-shrink-0">{label}</span>
                <span className="text-gray-900 text-right">{formatDate(value)}</span>
              </div>
            ))}
            <div className="flex justify-between items-center text-sm">
              <span className="text-gray-500">Deleted</span>
              <span className={`font-medium ${project.isDeleted ? 'text-red-600' : 'text-green-600'}`}>
                {project.isDeleted ? 'Yes' : 'No'}
              </span>
            </div>
          </div>
        </motion.div>
      </div>

      {/* Description */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.25 }}
        className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-4">
          <FaEdit className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">Description</h3>
        </div>
        <p className="text-gray-700 leading-relaxed">{project.description || '—'}</p>
      </motion.div>

      {/* Stage Scores */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.3 }}
        className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-5">
          <FaChartBar className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">Stage Scores</h3>
        </div>
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {(Object.entries(project.stageScores ?? {}) as [string, number | null][]).map(([key, score]) => (
            <div key={key} className="border border-gray-100 rounded-xl p-3 text-center">
              <p className={`text-2xl font-bold ${scoreColor(score)}`}>{score ?? '—'}</p>
              <p className="text-xs text-gray-500 mt-1">{STAGE_SCORE_LABELS[key] || key}</p>
            </div>
          ))}
        </div>
      </motion.div>

      {/* Generated Documents */}
      <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.35 }}
        className="bg-white rounded-2xl shadow-lg p-6">
        <div className="flex items-center gap-3 mb-5">
          <FaSearch className="h-5 w-5 text-primary" />
          <h3 className="text-lg font-semibold text-gray-900">Generated Documents</h3>
        </div>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
          {DOC_ENTRIES.map(({ key, label }) => {
            const doc = (project.document ?? {})[key] as { pdf: string | null } | undefined;
            const hasPdf = !!doc?.pdf;
            return (
              <div key={key} className={`border rounded-xl p-4 flex items-center justify-between ${hasPdf ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-gray-50'}`}>
                <div className="flex items-center gap-2">
                  {hasPdf
                    ? <FaCheckCircle className="text-green-500 flex-shrink-0" />
                    : <FaTimes className="text-gray-300 flex-shrink-0" />}
                  <span className="text-sm font-medium text-gray-800">{label}</span>
                </div>
                {hasPdf && doc?.pdf && (
                  <a href={doc.pdf} target="_blank" rel="noopener noreferrer"
                    className="text-xs text-blue-600 hover:text-blue-800 font-medium ml-2 flex-shrink-0">
                    PDF
                  </a>
                )}
              </div>
            );
          })}

          {/* Investor Deck */}
          <div className={`border rounded-xl p-4 ${project.document?.investorDeck?.ppt || project.document?.investorDeck?.pdf ? 'border-green-200 bg-green-50' : 'border-gray-100 bg-gray-50'}`}>
            <div className="flex items-center justify-between">
            <div className="flex items-center gap-2">
              {(project.document?.investorDeck?.ppt || project.document?.investorDeck?.pdf)
                ? <FaCheckCircle className="text-green-500 flex-shrink-0" />
                : <FaTimes className="text-gray-300 flex-shrink-0" />}
              <span className="text-sm font-medium text-gray-800">Two-page Pitch Summary</span>
            </div>
              {project.document?.investorDeck?.ppt && (
                <a href={project.document.investorDeck.ppt} target="_blank" rel="noopener noreferrer"
                  className="text-xs text-blue-600 hover:text-blue-800 font-medium">PPT</a>
              )}
            </div>
          </div>

          
        </div>
      </motion.div>


      {/* User Personas */}
      {project.userPersona?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.45 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaUsers className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">User Personas</h3>
          </div>
          <div className="space-y-4">
            {project.userPersona.map((persona, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4">
                <h4 className="font-semibold text-gray-900 mb-4">{persona.name}</h4>
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  {[
                    { label: 'Demographics', items: persona.demographics ?? [], dot: 'bg-blue-400' },
                    { label: 'Behaviors', items: persona.behaviors ?? [], dot: 'bg-gray-400' },
                    { label: 'Goals', items: persona.goals ?? [], dot: 'bg-green-400' },
                    { label: 'Pain Points', items: persona.pain_points ?? [], dot: 'bg-red-400' },
                  ].map(({ label, items, dot }) => (
                    <div key={label}>
                      <p className="text-xs font-medium text-gray-500 mb-1">{label}</p>
                      <ul className="space-y-1">
                        {items.map((item, ii) => (
                          <li key={ii} className="flex items-start gap-1.5 text-sm text-gray-700">
                            <span className={`w-1.5 h-1.5 ${dot} rounded-full mt-1.5 flex-shrink-0`} />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Value Propositions */}
      {project.valueProps?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.5 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaCheckCircle className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Value Propositions</h3>
          </div>
          <div className="space-y-3">
            {project.valueProps.map((vp, i) => (
              <div key={i} className="border-l-4 border-primary pl-4 py-2">
                <p className="text-xs font-semibold text-primary mb-1">{vp.persona}</p>
                <p className="text-sm text-gray-700">{vp.value_proposition}</p>
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Features */}
      {project.features?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.55 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaEye className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Features</h3>
          </div>
          <div className="space-y-3">
            {project.features.map((feature, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4">
                <div className="flex items-center justify-between mb-2">
                  <p className="font-medium text-gray-900 text-sm">{feature.name}</p>
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium flex-shrink-0 ml-2 ${
                    feature.priority === 'High' ? 'bg-red-100 text-red-700' :
                    feature.priority === 'Medium' ? 'bg-yellow-100 text-yellow-700' :
                    'bg-green-100 text-green-700'
                  }`}>{feature.priority}</span>
                </div>
                {feature.non_technical_details && (
                  <p className="text-xs text-gray-600 mb-1">{feature.non_technical_details}</p>
                )}
                {feature.technical_details && (
                  <p className="text-xs text-gray-500 italic">{feature.technical_details}</p>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Core Business Questions */}
      {project.coreBusinessQuestion?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.65 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaProjectDiagram className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Core Business Questions</h3>
          </div>
          <div className="space-y-3">
            {project.coreBusinessQuestion.map((q, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4">
                <p className="text-xs text-gray-500 mb-1">Answer</p>
                <p className="text-sm font-medium text-gray-900 mb-2">{q.answer}</p>
                {(q.answerArr ?? []).length > 0 && (
                  <div className="flex flex-wrap gap-1.5">
                    {(q.answerArr ?? []).map((a, ai) => (
                      <span key={ai} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded-full text-xs">{a}</span>
                    ))}
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Follow-up Questions */}
      {project.followUpQuestion?.length > 0 && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.7 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaEnvelope className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Follow-up Questions</h3>
          </div>
          <div className="space-y-4">
            {project.followUpQuestion.map((q, i) => (
              <div key={i} className="border border-gray-100 rounded-xl p-4 space-y-2">
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Question</p>
                  <p className="text-sm font-medium text-gray-900">{q.question}</p>
                </div>
                <div>
                  <p className="text-xs text-gray-500 mb-0.5">Answer</p>
                  <p className="text-sm text-gray-700">{q.answer}</p>
                </div>
                {q.suggestedAnswer && q.suggestedAnswer !== q.answer && (
                  <div>
                    <p className="text-xs text-gray-400 mb-0.5">Suggested Answer</p>
                    <p className="text-xs text-gray-500 italic">{q.suggestedAnswer}</p>
                  </div>
                )}
              </div>
            ))}
          </div>
        </motion.div>
      )}

      {/* Ad Content */}
      {project.ads && (
        <motion.div initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: 0.75 }}
          className="bg-white rounded-2xl shadow-lg p-6">
          <div className="flex items-center gap-3 mb-5">
            <FaChartBar className="h-5 w-5 text-primary" />
            <h3 className="text-lg font-semibold text-gray-900">Ad Content</h3>
          </div>
          <div className="space-y-4">
            {project.ads.meta?.instagram && (
              <div className="border border-gray-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-pink-600 mb-2 uppercase tracking-wide">Instagram</p>
                <p className="text-sm text-gray-700 mb-3">{project.ads.meta.instagram.content}</p>
                {project.ads.meta.instagram.imageUrl && (
                  <img src={project.ads.meta.instagram.imageUrl} alt="Instagram ad"
                    className="w-40 h-auto rounded-lg border border-gray-100" />
                )}
              </div>
            )}
            {project.ads.x && (
              <div className="border border-gray-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-blue-500 mb-2 uppercase tracking-wide">X (Twitter)</p>
                <p className="text-sm text-gray-700 mb-3">{project.ads.x.content}</p>
                {project.ads.x.imageUrl && (
                  <img src={project.ads.x.imageUrl} alt="X ad"
                    className="w-40 h-auto rounded-lg border border-gray-100" />
                )}
              </div>
            )}
            {project.ads.google && (
              <div className="border border-gray-100 rounded-xl p-4">
                <p className="text-xs font-semibold text-green-600 mb-3 uppercase tracking-wide">Google Ads</p>
                <div className="space-y-3">
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Headlines</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(project.ads.google.content.headlines ?? []).map((h, hi) => (
                        <span key={hi} className="px-2 py-0.5 bg-gray-100 text-gray-700 rounded text-xs">{h}</span>
                      ))}
                    </div>
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Descriptions</p>
                    {(project.ads.google.content.descriptions ?? []).map((d, di) => (
                      <p key={di} className="text-xs text-gray-700 mb-0.5">{d}</p>
                    ))}
                  </div>
                  <div>
                    <p className="text-xs text-gray-500 mb-1">Keywords</p>
                    <div className="flex flex-wrap gap-1.5">
                      {(project.ads.google.content.keywords ?? []).map((k, ki) => (
                        <span key={ki} className="px-2 py-0.5 bg-blue-50 text-blue-600 rounded text-xs">{k}</span>
                      ))}
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>
        </motion.div>
      )}

    </div>
  );
};

export default ProjectDetail;
